import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Edit, Save, Loader2, Calendar, User, FileText, AlertTriangle, CheckCircle2, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import { getAuditEngagementById, updateAuditEngagement, AUDIT_TYPES, AUDIT_STATUSES } from '@/services/audit';

const statusColor = (s) => ({ planning: 'bg-blue-500/10 text-blue-700', in_progress: 'bg-amber-500/10 text-amber-700', review: 'bg-violet-500/10 text-violet-700', completed: 'bg-emerald-500/10 text-emerald-700', cancelled: 'bg-gray-500/10 text-gray-500' })[s] || 'bg-gray-500/10 text-gray-500';

export default function AuditEngagementDetailPage() {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAuditEngagementById(engagementId);
      setEngagement(data);
      setForm(data);
    } catch (err) {
      toast.error('Failed to load engagement');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  const isPastAudit = engagement && (engagement.status === 'completed' || engagement.status === 'cancelled');

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateAuditEngagement(engagementId, {
        title: form.title,
        description: form.description,
        status: form.status,
        audit_type: form.audit_type,
        lead_auditor: form.lead_auditor,
        scope: form.scope,
        objectives: form.objectives,
      });
      toast.success('Engagement updated');
      setEditing(false);
      load();
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  if (!engagement) return <div className="p-6 text-center opacity-50">Engagement not found</div>;

  return (
    <div className="p-6 space-y-6" data-testid="engagement-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/grc/audit/engagements')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold">{engagement.title || 'Untitled Engagement'}</h1>
            <p className="text-sm opacity-60">{engagement.audit_type || 'Audit'} Engagement</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusColor(engagement.status)}>{(engagement.status || 'planning').replace('_', ' ')}</Badge>
          {!isPastAudit && !editing && (
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-btn"><Edit className="w-4 h-4 mr-2" /> Edit</Button>
          )}
          {isPastAudit && (
            <Badge variant="secondary" className="text-xs">Read-only (past audit)</Badge>
          )}
        </div>
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Info */}
        <Card className="lg:col-span-2" data-testid="engagement-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Engagement Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div><Label>Title</Label><Input value={form.title || ''} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="edit-title" /></div>
                <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Audit Type</Label>
                    <Select value={form.audit_type || ''} onValueChange={v => setForm(p => ({ ...p, audit_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{AUDIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Status</Label>
                    <Select value={form.status || ''} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{AUDIT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Lead Auditor</Label><Input value={form.lead_auditor || ''} onChange={e => setForm(p => ({ ...p, lead_auditor: e.target.value }))} /></div>
                <div><Label>Scope</Label><Textarea value={form.scope || ''} onChange={e => setForm(p => ({ ...p, scope: e.target.value }))} rows={2} /></div>
                <div><Label>Objectives</Label><Textarea value={form.objectives || ''} onChange={e => setForm(p => ({ ...p, objectives: e.target.value }))} rows={2} /></div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={saving} data-testid="save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save</Button>
                  <Button variant="outline" onClick={() => { setEditing(false); setForm(engagement); }}><X className="w-4 h-4 mr-2" /> Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <div><Label className="text-xs opacity-50">Description</Label><p className="text-sm mt-1">{engagement.description || 'No description'}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs opacity-50">Audit Type</Label><p className="text-sm mt-1 capitalize">{(engagement.audit_type || '-').replace('_', ' ')}</p></div>
                  <div><Label className="text-xs opacity-50">Lead Auditor</Label><p className="text-sm mt-1">{engagement.lead_auditor || '-'}</p></div>
                </div>
                <div><Label className="text-xs opacity-50">Scope</Label><p className="text-sm mt-1">{engagement.scope || 'Not defined'}</p></div>
                <div><Label className="text-xs opacity-50">Objectives</Label><p className="text-sm mt-1">{engagement.objectives || 'Not defined'}</p></div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sidebar - Timeline & Meta */}
        <Card data-testid="engagement-meta-card">
          <CardHeader className="pb-3"><CardTitle className="text-base">Timeline & Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 opacity-50" /><span className="opacity-50">Created:</span><span>{engagement.created_at ? new Date(engagement.created_at).toLocaleDateString() : '-'}</span></div>
            {engagement.start_date && <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 opacity-50" /><span className="opacity-50">Start:</span><span>{new Date(engagement.start_date).toLocaleDateString()}</span></div>}
            {engagement.end_date && <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 opacity-50" /><span className="opacity-50">End:</span><span>{new Date(engagement.end_date).toLocaleDateString()}</span></div>}
            {engagement.created_by && <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 opacity-50" /><span className="opacity-50">Created by:</span><span>{engagement.created_by}</span></div>}
            {engagement.framework_id && <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 opacity-50" /><span className="opacity-50">Framework:</span><span>{engagement.framework_id}</span></div>}

            {/* Findings summary if available */}
            {engagement.findings_count > 0 && (
              <div className="mt-4 p-3 rounded-lg border">
                <p className="text-xs font-medium mb-2">Findings</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 rounded bg-red-500/10"><p className="font-bold text-red-600">{engagement.critical_findings || 0}</p><p className="text-[10px] opacity-50">Critical</p></div>
                  <div className="text-center p-2 rounded bg-orange-500/10"><p className="font-bold text-orange-600">{engagement.high_findings || 0}</p><p className="text-[10px] opacity-50">High</p></div>
                </div>
              </div>
            )}

            {/* Audit Trail */}
            {engagement.timeline && engagement.timeline.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium mb-2">Activity</p>
                <div className="space-y-2">
                  {engagement.timeline.slice(0, 5).map((event, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5" />
                      <div><p>{event.action || event.description}</p><p className="opacity-40">{event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
